import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarSign, Users, TrendingUp, CheckCircle, Zap, BarChart3, Gift, ArrowRight, Sparkles, ChevronDown } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { AffiliateApplicationForm } from "@/components/affiliate/AffiliateApplicationFormEnhanced";
import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";
import { getFAQPageSchema, getHowToSchema, getBreadcrumbSchema } from "@/lib/schema";

export const AffiliateProgram = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  // Auto-open application form if user just logged in to apply
  useEffect(() => {
    const fromAuth = searchParams.get('fromAuth');
    if (user && fromAuth === 'true') {
      setShowApplicationForm(true);
      // Clean up URL
      navigate('/affiliate', { replace: true });
    }
  }, [user, searchParams, navigate]);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "30% Lifetime Commission",
      description: "Earn recurring revenue for every subscriber you refer",
      highlight: "$5.70/mo or $54/yr",
    },
    {
      icon: TrendingUp,
      title: "Passive Income",
      description: "Keep earning as long as they stay subscribed",
      highlight: "Forever",
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics",
      description: "Track clicks, conversions, and earnings instantly",
      highlight: "Live Dashboard",
    },
  ];

  const features = [
    { icon: Zap, text: "60-day cookie window" },
    { icon: Gift, text: "$50 minimum payout" },
    { icon: Users, text: "Dedicated support" },
    { icon: Sparkles, text: "Referrals get 7-day free trial" },
  ];

  const faqs = [
    {
      question: "How much can I really earn?",
      answer: "You earn 30% recurring commission on every subscription. For example, if you refer 20 customers on the monthly plan ($19/mo), that's $114/month in passive income. On the annual plan ($108/yr), 20 referrals would earn you $648/year. The more you refer, the more you earn - and it's recurring income as long as they stay subscribed."
    },
    {
      question: "When do I get paid?",
      answer: "Commissions are available 30 days after a successful conversion (Net-30). We pay out monthly via PayPal once you reach the $50 minimum threshold. For example, if you earn $75 in January, you'll receive your payout in early March."
    },
    {
      question: "How long does the cookie last?",
      answer: "Our affiliate cookie lasts 60 days. This means if someone clicks your link today but signs up 45 days later, you still get credit for that referral. This gives your audience plenty of time to make a decision."
    },
    {
      question: "What marketing materials do you provide?",
      answer: "Once approved, you'll get access to professionally designed banners, email templates, social media graphics, and pre-written copy. We also provide product screenshots, case studies, and conversion tips to help you succeed."
    },
    {
      question: "Who is a good fit for this program?",
      answer: "Anyone with an audience interested in entrepreneurship, product validation, startups, or marketing. This includes bloggers, YouTubers, newsletter writers, course creators, consultants, and SaaS reviewers. If your audience builds products or validates ideas, they'll love WillTheyConvert."
    },
    {
      question: "Can I promote to my email list or social media?",
      answer: "Absolutely! You can promote via email lists, social media, blog posts, YouTube videos, podcasts, or any channel where you have an audience. We only ask that you follow ethical marketing practices and don't use spam or misleading claims."
    },
    {
      question: "How do I track my performance?",
      answer: "You'll get access to a real-time affiliate dashboard showing clicks, conversions, earnings, and payout history. You can see exactly which campaigns are performing best and optimize accordingly."
    },
    {
      question: "What if my referral cancels their subscription?",
      answer: "You earn commissions only while your referrals remain subscribed. If they cancel, you won't receive future commissions from that customer. However, this motivates us to build a great product that customers love and stick with!"
    },
    {
      question: "How long does approval take?",
      answer: "Most applications are reviewed within 24-48 hours. We review each application to ensure quality and alignment with our brand. Once approved, you'll receive your unique affiliate link and access to all marketing materials immediately."
    },
    {
      question: "Is there a limit to how much I can earn?",
      answer: "No limits! There's no cap on the number of referrals or earnings. The more value you provide to your audience, the more you can earn. Some affiliates make a few hundred dollars per month, others make thousands - it all depends on your effort and audience size."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      <SEO
        title="Affiliate Program - Earn 30% Recurring Commission | WillTheyConvert"
        description="Join our affiliate program and earn 30% lifetime commissions on every referral. Share WillTheyConvert with your audience and build passive income."
        keywords="affiliate program, referral program, earn commissions, passive income, startup tools affiliate"
        canonicalUrl="/affiliate"
        jsonLd={[
          getBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Affiliate Program", url: "/affiliate" },
          ]),
          getFAQPageSchema(faqs),
          getHowToSchema(
            "How to Earn as a WillTheyConvert Affiliate",
            "Join our affiliate program and start earning 30% recurring commissions.",
            [
              { name: "Apply to Join", text: "Fill out our simple application form to become an affiliate." },
              { name: "Get Your Link", text: "Once approved, receive your unique affiliate tracking link." },
              { name: "Share with Your Audience", text: "Promote WillTheyConvert to your email list, social media, or blog." },
              { name: "Earn Commissions", text: "Get 30% recurring commission on every subscription you refer." },
            ]
          ),
        ]}
      />
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl">

        {/* Hero Section - Modern & Minimal */}
        <motion.div
          className="text-center mb-16 md:mb-24"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-full mb-6"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <Sparkles className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">Affiliate Program</span>
          </motion.div>
          <motion.h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-gray-900"
            variants={fadeInUp}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Earn While You
            <span className="block mt-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent">
              Help Others Grow
            </span>
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
            variants={fadeInUp}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Join our affiliate program and earn <strong>30% recurring commissions</strong> for life.
            Share a tool you believe in, build passive income.
          </motion.p>

          {/* CTA Button */}
          <motion.div
            variants={fadeInUp}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8"
          >
            <Button
              size="lg"
              onClick={() => {
                if (user) {
                  setShowApplicationForm(true);
                } else {
                  navigate('/auth?returnTo=/affiliate');
                }
              }}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-lg px-10 h-14 rounded-full shadow-lg hover:shadow-xl font-semibold transition-all transform hover:scale-105"
            >
              {user ? 'Apply Now' : 'Sign In to Apply'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </motion.div>

        {/* Quick Stats - Sleek Cards */}
        <motion.div
          className="grid md:grid-cols-3 gap-6 mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                variants={fadeInUp}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -8 }}
              >
                <Card className="group relative overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-xl hover:border-indigo-300 transition-all duration-300 p-8 h-full rounded-3xl">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-100/50 to-purple-100/50 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
                  <div className="relative">
                    <motion.div
                      className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mb-4 shadow-md"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{benefit.description}</p>
                    <div className="inline-block px-3 py-1 bg-indigo-50 rounded-full">
                      <span className="text-sm font-semibold text-indigo-700">{benefit.highlight}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Commission Breakdown - Modern Minimal */}
        <motion.div
          className="mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
        >
          <motion.div
            className="text-center mb-12"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900">Partner Payouts</h2>
            <p className="text-gray-600">Earn 30% recurring commission on every referral, every month</p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Card className="relative overflow-hidden border-2 border-indigo-200 bg-gradient-to-br from-white to-indigo-50/30 p-8 hover:shadow-lg transition-shadow h-full rounded-3xl">
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="text-sm font-medium text-indigo-700 mb-2">Monthly Subscription Plan</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-5xl font-bold text-gray-900">$5.70</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-gray-600">You earn per referral on $19/mo plan</p>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Example earnings:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">10 referrals</span>
                        <span className="font-semibold text-gray-900">$57/mo</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">50 referrals</span>
                        <span className="font-semibold text-gray-900">$285/mo</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Card className="relative overflow-hidden border-2 border-violet-200 bg-gradient-to-br from-white to-violet-50/30 p-8 hover:shadow-lg transition-shadow h-full rounded-3xl">
                <div className="absolute top-0 right-0 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-sm font-medium text-violet-700">Annual Subscription Plan</div>
                    <motion.span
                      className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      Popular
                    </motion.span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-5xl font-bold text-gray-900">$54</span>
                    <span className="text-gray-500">/year</span>
                  </div>
                  <p className="text-sm text-gray-600">You earn per referral on $108/yr plan</p>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Example earnings:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">10 referrals</span>
                        <span className="font-semibold text-gray-900">$540/yr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">50 referrals</span>
                        <span className="font-semibold text-gray-900">$2,700/yr</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* How It Works - Clean & Simple */}
        <motion.div
          className="mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
        >
          <motion.div
            className="text-center mb-12"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900">How It Works</h2>
            <p className="text-gray-600">Start earning in 4 simple steps</p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <motion.div
              className="grid md:grid-cols-4 gap-6"
              variants={staggerContainer}
            >
              {[
                { num: "01", title: "Apply", desc: "Quick application form" },
                { num: "02", title: "Get Approved", desc: "Usually within 24 hours" },
                { num: "03", title: "Share Link", desc: "Promote to your audience" },
                { num: "04", title: "Earn Money", desc: "Get paid monthly" },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  className="relative"
                  variants={fadeInUp}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-center">
                    <motion.div
                      className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white font-bold text-lg mb-4 shadow-lg"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      {step.num}
                    </motion.div>
                    <h3 className="font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.desc}</p>
                  </div>
                  {i < 3 && (
                    <motion.div
                      className="hidden md:block absolute top-6 left-full w-full"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                    >
                      <ArrowRight className="w-5 h-5 text-gray-300 mx-auto" />
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Features - Minimal Icons */}
        <motion.div
          className="mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
        >
          <Card className="border-0 bg-gray-900 text-white p-8 md:p-12 rounded-3xl">
            <motion.div
              className="text-center mb-10"
              variants={fadeInUp}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-3">Program Benefits</h2>
              <p className="text-gray-400">Everything you need to succeed</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {features.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-indigo-400" />
                    </div>
                    <span className="text-gray-100">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 pt-10 border-t border-white/10">
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-white">Net-30 payments</strong>
                    <p className="text-gray-400">Commissions available 30 days after conversion</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-white">Monthly payouts via PayPal</strong>
                    <p className="text-gray-400">Reliable, on-time payments every month</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-white">Marketing materials provided</strong>
                    <p className="text-gray-400">Banners, copy, and assets ready to use</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-white">No approval required for referrals</strong>
                    <p className="text-gray-400">All legitimate signups count automatically</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* CTA Section - Modern */}
        <motion.div
          className="text-center mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
        >
          <Card className="border-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-600 text-white p-12 md:p-16 shadow-2xl overflow-hidden relative rounded-3xl">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            <div className="relative z-10">
              <motion.h2
                className="text-3xl md:text-4xl font-bold mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                Ready to Start Earning?
              </motion.h2>
              <motion.p
                className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                Join our growing community of affiliates earning passive income
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="lg"
                    onClick={() => {
                      if (user) {
                        setShowApplicationForm(true);
                      } else {
                        navigate('/auth?returnTo=/affiliate');
                      }
                    }}
                    className="bg-white text-indigo-700 hover:bg-gray-100 text-lg px-8 h-14 rounded-full shadow-lg font-semibold transition-all"
                  >
                    {user ? 'Apply Now' : 'Sign In to Apply'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </motion.div>
              <motion.p
                className="text-sm text-indigo-100 mt-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                {user ? 'Approval typically within 24 hours' : 'Free account required'}
              </motion.p>
            </div>
          </Card>
        </motion.div>

        {/* Application Form Dialog */}
        <Dialog open={showApplicationForm} onOpenChange={setShowApplicationForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="sr-only">
              <DialogTitle>Apply to Affiliate Program</DialogTitle>
              <DialogDescription>
                Fill out the application form to join our affiliate program
              </DialogDescription>
            </DialogHeader>
            <AffiliateApplicationForm
              userEmail={user?.email}
              onSuccess={() => {
                setTimeout(() => {
                  setShowApplicationForm(false);
                }, 2000);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* FAQ Section - After CTA */}
        <motion.div
          className="mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
        >
          <motion.div
            className="text-center mb-12"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about the program</p>
          </motion.div>

          <motion.div
            className="max-w-3xl mx-auto space-y-3"
            variants={staggerContainer}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                transition={{ duration: 0.4 }}
              >
                <FAQItem question={faq.question} answer={faq.answer} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <motion.div
        initial={false}
        animate={{
          backgroundColor: isOpen ? 'rgb(249, 250, 251)' : 'rgb(255, 255, 255)',
        }}
        transition={{ duration: 0.3 }}
      >
        <Card className="border border-gray-200 hover:border-indigo-300 transition-colors overflow-hidden rounded-2xl">
          <CollapsibleTrigger className="w-full text-left">
            <motion.div
              className="flex items-center justify-between p-6 cursor-pointer group"
              whileHover={{ x: 4 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <h3 className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors pr-4">
                {question}
              </h3>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
              </motion.div>
            </motion.div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 pb-6 pt-0"
            >
              <p className="text-gray-600 leading-relaxed">{answer}</p>
            </motion.div>
          </CollapsibleContent>
        </Card>
      </motion.div>
    </Collapsible>
  );
};
